import { ArrowLeft } from 'lucide-react'
import type { RefObject } from 'react'
import type { SearchResult } from '@agentwiki/shared'
import type { SearchSource, SearchType } from '../../hooks/use-search'
import { cn } from '../../lib/utils'
import { EmptyState } from './search-workspace-parts'
import { SearchResultRow } from './search-result-row'

export function SearchWorkspaceHeader({ isDark, onBack }: { isDark: boolean; onBack: () => void }) {
  return (
    <div
      className={cn(
        'border-b px-4 py-4 md:px-6',
        isDark ? 'border-white/[0.06]' : 'border-neutral-200',
      )}
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className={cn(
            'aw-search-control cursor-pointer rounded-lg p-2 md:p-1.5',
            isDark
              ? 'text-neutral-400 hover:bg-surface-3'
              : 'text-neutral-500 hover:bg-neutral-100',
          )}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5 md:h-4 md:w-4" aria-hidden="true" />
        </button>
        <h1
          className={cn('text-lg font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}
        >
          Search
        </h1>
      </div>
    </div>
  )
}

interface SearchResultsSectionProps {
  hasQuery: boolean
  isError: boolean
  isFetching: boolean
  errorMessage: string
  results: SearchResult[]
  searchId?: string
  source: SearchSource
  searchType: SearchType
  isDark: boolean
  onOpen: (
    result: SearchResult,
    position: number,
    searchId?: string,
    navigation?: 'current' | 'new-tab',
  ) => void
  onRetry?: () => void
  headingRef?: RefObject<HTMLHeadingElement | null>
}

export function SearchResultsSection({
  hasQuery,
  isError,
  isFetching,
  results,
  searchId,
  source,
  searchType,
  isDark,
  onOpen,
  onRetry,
  headingRef,
}: SearchResultsSectionProps) {
  return (
    <section
      aria-label="Search results"
      aria-busy={isFetching ? 'true' : 'false'}
      className="flex flex-col gap-3"
    >
      <h2 ref={headingRef} tabIndex={-1} className="sr-only">
        Search results
      </h2>
      {!hasQuery && (
        <EmptyState
          title="Enter at least two characters"
          detail="Search uses workspace documents and indexed extracted file content by default."
          isDark={isDark}
        />
      )}
      {hasQuery && isFetching && (
        <div role="status" aria-live="polite">
          <EmptyState
            title="Searching knowledge…"
            detail="Retrieving the current workspace results."
            isDark={isDark}
          />
        </div>
      )}
      {hasQuery && isError && (
        <div role="alert" className="grid gap-3">
          <EmptyState
            title="Search unavailable"
            detail="Search could not retrieve results. Try again."
            isDark={isDark}
          />
          {onRetry && (
            <button
              type="button"
              className={cn(
                'aw-search-control mx-auto rounded-md border px-4 text-sm font-medium',
                isDark
                  ? 'border-white/[0.12] text-neutral-100 hover:bg-surface-2'
                  : 'border-neutral-300 text-neutral-800 hover:bg-neutral-100',
              )}
              onClick={onRetry}
            >
              Try Search again
            </button>
          )}
        </div>
      )}
      {hasQuery && !isError && !isFetching && results.length === 0 && (
        <EmptyState
          title="No results"
          detail="Try All sources or switch between Hybrid, Keyword, and Semantic."
          isDark={isDark}
        />
      )}
      {!isError &&
        !isFetching &&
        results.map((result, index) => (
          <SearchResultRow
            key={`${result.resultType ?? 'document'}-${result.id}`}
            result={result}
            index={index}
            searchId={searchId}
            source={source}
            searchType={searchType}
            isDark={isDark}
            onOpen={onOpen}
          />
        ))}
    </section>
  )
}
